<template>
  <div class="profit-loss">
    <div class="expire-date">
      {{ $t('expire-date') }}：{{ expireDate }}
    </div>
    <svg
      id="profitLoss"
      ref="svg"
      width="240"
      height="120"
      preserveAspectRatio="none"
    />
  </div>
</template>
<script>
import d3 from './d3-lib'
import { round } from 'utils/common'

export default {
  name: 'ProfitLossCurves',
  props: {
    curveData: Array,
    expireDate: String,
    digits: Number
  },
  computed: {
    language() {
      return this.$store.state.settings.language
    },
    theme() {
      const theme = this.$store.state.settings.theme
      return theme || 'theme-dark'
    }
  },
  mounted() {
    this.draw()
  },
  watch: {
    curveData() {
      this.draw()
    },
    language() {
      this.draw()
    },
    theme() {
      this.draw()
    }
  },
  methods: {
    draw() {
      const width = 240
      const height = 120
      const margin = { left: 30, top: 20, right: 30, bottom: 15 }
      const gWidth = width - 20
      const gHeight = height - margin.top - margin.bottom

      const svg = d3.select('#profitLoss')
      svg.html('')
      const lineG = svg
        .append('g')
        .attr('transform', 'translate(0,' + margin.top + ')')

      const scaleX = d3
        .scaleLinear()
        .domain(d3.extent(this.curveData, d => d[0]))
        .range([0, gWidth])
      const scaleY = d3
        .scaleLinear()
        .domain(d3.extent(this.curveData, d => d[1]))
        .range([gHeight, 0])

      const lineGenerator = d3
        .line()
        .x(d => {
          return scaleX(d[0])
        })
        .y(d => {
          return scaleY(d[1])
        })

      const centerLine = d3.axisBottom(scaleX).tickSize(0)
      const xAxis = d3
        .axisBottom(scaleX)
        .tickValues(
          this.curveData.map(d => {
            return d[0]
          })
        )
        .tickFormat(d => {
          return d.toFixed(this.digits)
        })
      const yAxis = d3
        .axisLeft(scaleY)
        .tickValues(
          this.curveData.map(d => {
            return d[1]
          })
        )
        .tickFormat(d => {
          const num = Math.abs(d)
          if (num >= 1000 && num <= 100000) {
            return round(d / 1000, 2) + 'K'
          } else if (num < 1000) {
            return d
          } else if (num >= 100000 && num <= 1000000000) {
            return round(d / 1000000, 2) + 'M'
          } else if (num > 1000000000) {
            return round(d / 1000000000, 2) + 'B'
          }
          return d
        })
      const isZero = this.curveData[0][1] === 0 && this.curveData[3][1] === 0
      const MAX =
        this.curveData[0][1] > 0 ? this.curveData[0][1] : this.curveData[3][1]
      const RATE = isZero
        ? 1 / 2
        : MAX /
          (Math.abs(this.curveData[0][1]) + Math.abs(this.curveData[3][1]))
      lineG
        .append('g')
        .call(centerLine)
        .attr('transform', 'translate(0,' + gHeight * RATE + ')')
        .attr('class', 'center-line')

      lineG
        .append('g')
        .call(xAxis)
        .attr('transform', 'translate(0,' + (height - margin.top + 5) + ')')
        .attr('class', 'x-line')
        .append('text')
        .text(this.$t('trade.profit-loss-curves.stock-price'))
        .attr('class', 'x-title')
        .attr('transform', 'translate(' + width + ', ' + (margin.top - 2) + ')')
        .attr('text-anchor', 'end')
      const threeLen = this.curveData[2][0].toFixed(0).length
      if (threeLen >= 5 && !isZero) {
        d3.selectAll('.x-line .tick text').attr('dx', (d, i) => {
          if (i === 1) {
            return '-0.5em'
          }
        })
      }

      lineG
        .append('g')
        .call(yAxis)
        .attr('transform', 'translate(0, ' + (isZero ? -gHeight / 2 : 0) + ')')
        .append('text')
        .text(this.$t('trade.profit-loss-curves.profit-loss'))
        .attr('class', 'y-title')
        .attr('dy', isZero ? '1em' : '-2.5em')
        .attr('dx', '-10px')
        .attr('text-anchor', 'end')
      if (isZero) return false
      lineG
        .append('path')
        .attr('d', lineGenerator(this.curveData))
        .attr('transform', 'translate(5, 0)')
        .attr('class', 'line-path')
    }
  }
}
</script>
<style lang="less">
@import '~uikit';
.profit-loss {
  width: 290px;
  margin: 0 auto;
  text-align: center;
  .expire-date {
    color: #838d9e;
    text-align: right;
    width: 285px;
  }
  svg {
    margin: 5px 0 20px 40px;
    border: 1px solid var(--light-grey);
    overflow: visible;
  }
  .line-path {
    fill: none;
    stroke: var(--default-font-color);
    stroke-width: 1;
  }

  .y-title,
  .x-title {
    fill: #838d9e;
    font-size: 12px;
  }

  .domain,
  .tick {
    stroke: transparent;
    line {
      stroke: transparent;
    }
    text {
      fill: #838d9e;
      font-size: 12px;
    }
  }
  .center-line {
    .domain {
      stroke: var(--red);
    }
    .tick {
      display: none;
    }
  }
  .x-line {
    // line {
    //   stroke: #838D9E;
    // }
    .tick {
      &:first-of-type,
      &:last-of-type {
        line {
          stroke: transparent;
        }
        text {
          display: none;
        }
      }
    }
  }
  // .x-line .tick:first-of-type,
  // .x-line .tick:last-of-type {
  //   text {
  //     display: none;
  //   }
  // }
}
</style>
